import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Globe, Fuel } from 'lucide-react';
import { productsAPI } from '../services/api';
import { vehicleStore, useSelectedVehicle } from '../services/vehicleStore';
import { useT } from '../services/i18n';
import { vehicleEnergy, energyLabel, fuelTypesLabel } from '../services/fuel';

// { product_id: 'fits' | 'no' | 'fuel' | 'universal' } for the cart lines against the selected vehicle
export const useCartFit = (items, vehicle) => {
  const [fit, setFit] = useState({});
  const [meta, setMeta] = useState({ energy: null, fuel_types: {} });
  const ids = [...new Set((items || []).map(i => i.product_id))].sort().join(',');
  const key = vehicle ? `${vehicle.brand}|${vehicle.model}|${vehicle.engine?.name || ''}` : '';
  useEffect(() => {
    if (!ids || !vehicle) { setFit({}); return; }
    let cancelled = false;
    productsAPI.fitCheck(ids.split(','), vehicleStore.toParams(vehicle))
      .then(d => { if (!cancelled) { setFit(d.results || {}); setMeta({ energy: d.energy || null, fuel_types: d.fuel_types || {} }); } })
      .catch(() => { if (!cancelled) setFit({}); });
    return () => { cancelled = true; };
  }, [ids, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return Object.assign(fit, { __meta: meta });
};

export const isCartMismatch = (status) => status === 'no' || status === 'fuel';

const vehicleName = (v) => `${v.brand} ${v.model}`;

export const CartFitBadge = ({ status, vehicle, productId, fuelTypes = [] }) => {
  const { t } = useT();
  if (!vehicle || !status) return null;
  const v = vehicleName(vehicle);
  if (status === 'fits') return <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full" data-testid={`cart-fit-ok-${productId}`}><CheckCircle2 size={12} /> {t('cart.fitOk', { v })}</p>;
  if (status === 'universal') return <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-500" data-testid={`cart-fit-universal-${productId}`}><Globe size={12} /> {t('cart.fitUniversal')}</p>;
  if (status === 'fuel') {
    const ve = vehicleEnergy(vehicle.engine?.fuel);
    return (
      <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-red-800 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full" data-testid={`cart-fit-fuel-${productId}`}>
        <Fuel size={12} /> {t('cart.fitFuel', { e: fuelTypesLabel(t, fuelTypes), v, ve: ve ? energyLabel(t, ve) : '' })}
      </p>
    );
  }
  return (
    <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid={`cart-fit-no-${productId}`}>
      <AlertTriangle size={12} /> {t('cart.fitNo', { v })}
      <Link to={`/produit/${productId}`} className="underline hover:text-amber-900" data-testid={`cart-fit-check-${productId}`}>{t('cart.fitCheck')}</Link>
    </p>
  );
};

// Summary banner: « n article(s) non confirmé(s) pour votre véhicule »
export const CartFitWarning = ({ items, testId = 'cart-fit-warning' }) => {
  const { t } = useT();
  const vehicle = useSelectedVehicle();
  const fit = useCartFit(items, vehicle);
  if (!vehicle) return null;
  const n = (items || []).filter(i => isCartMismatch(fit[i.product_id])).length;
  if (!n) return null;
  return (
    <div className="mb-4 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid={testId}>
      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
      <span>{t('cart.fitWarn', { n, count: n, v: vehicleName(vehicle) })}</span>
    </div>
  );
};
